import { AxiosResponse } from 'axios';
import { App, inject, Ref, shallowReactive, shallowRef } from 'vue';

import useLoadingStore from 'lib/components/loading/store';
import { getCookie, setCookie } from 'lib/utils/cookie.util';

export type Locale = string;

export interface LanguageOption {
  /**
   * ISO 639-1 language code (e.g., "en" for English, "fr" for French).
   */
  isoCode?: string;

  /**
   * The name of the language in English (e.g., "English", "Français").
   */
  name: string;

  /**
   * The URL of the country's flag image.
   * Flags are sourced from https://flagcdn.com and are typically in SVG format for high-quality resolution.
   */
  flag?: string;
}

export interface I18n {
  /**
   * Locale message translation
   *
   * @param key - The translation key
   * @param params - A values of named interpolation
   * @param args - Positional interpolation values
   *
   * @returns translation message
   * @example `t('Hello {user}!', { user: 'John Doe' })` - returns: 'Hello John Doe!'
   * @example `t('Hello %s! I am %s.', "John Doe", "Peter Smith")` - returns: 'Hello John Doe! I am Peter Smith.'
   */
  t: {
    (key: string, params: Record<string, any>): string;
    (key: string, ...args: (string | number | boolean)[]): string;
  };

  /**
   * Change the locale dynamically
   *
   * @param newLocale - The locale to switch to
   */
  setLocale: (newLocale: Locale) => Promise<void>;

  /**
   * Get the current locale
   *
   * @returns Current locale
   */
  locale: Ref<Locale>;

  /**
   * Service methods handle fetch requests
   */
  service?: I18nService;
}

type MessageMap = Record<string, string>;
type MessageStore = Partial<Record<Locale, MessageMap>>;

export interface I18nService {
  /**
   * Fetch all translation messages for a specific locale.
   * @param locale The locale code (e.g., 'en', 'id').
   * @returns A promise resolving to a key-value record of messages.
   */
  getMessages(locale: string): Promise<AxiosResponse<{ data: MessageMap }>>;

  /**
   * Fetch all available lang options for LanguageDropdown and LanguageSwitcher
   *
   * @returns Promise Array of options
   */
  getLanguageOptions(): Promise<LanguageOption[]>;

  /**
   * Fetch single lang option meta data
   *
   * @param locale The locale code (e.g., 'en', 'id').
   * @returns Promise LanguageMeta
   */
  getLanguageOptionMeta(locale: string): Promise<LanguageOption>;

  /**
   * Translate a specific text to the target locale.
   * @param key Unique translation key.
   * @param locale Target locale code.
   * @returns A promise resolving to the translated string.
   */
  translateText(key: string, locale: string): Promise<string>;
}

/**
 * Initialy undefined to prevent $t translating single sentences when language packages not loaded yet
 */
const locale = shallowRef<Locale>();

const STORAGE_KEY = 'messages';

let i18nService: I18nService;

const loadStoredMessages = (): MessageStore => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const base: MessageStore = shallowReactive(loadStoredMessages());

const storedMessages = new Proxy(base, {
  get: (target, prop: string): MessageMap => {
    const stored = loadStoredMessages();
    const value = stored[prop as Locale];
    base[prop as Locale] = value;
    return value;
  },
  set: (target, prop: string, value): boolean => {
    base[prop as Locale] = value;
    saveMessages(base);
    return true;
  },
});

const saveMessages = (msgs: MessageStore): void => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(msgs));
};

/**
 * Fetch messages for a specific locale
 *
 * @param locale - The locale to fetch messages for
 * @returns Promise resolving to locale messages
 */
const fetchMessages = async (l: string): Promise<MessageMap> => {
  if (!(l in storedMessages)) {
    const { setLoading } = useLoadingStore();
    setLoading(true, 'Loading language package...');

    let messages: MessageMap = {};
    try {
      const { data } = await (i18nService?.getMessages?.(l) ?? {});

      messages = data.data;
    } catch {
      //
    } finally {
      setLoading(false);
    }

    return messages;
  }

  return storedMessages[l];
};

const t = (key: string, ...params: any[]): string => {
  if (!locale.value && !getCookie('lang')) return key; // Locale is initialy undefined, will be exist if messages loaded
  let message = storedMessages[locale.value]?.[key];

  /**
   * State when previously translated message is not found or error network
   * The message is the same as the key
   */
  const isWrongTranslation = message === key && locale.value !== 'en';

  if (!message || isWrongTranslation) {
    message = key;
    i18nService
      ?.translateText(key, locale.value)
      // eslint-disable-next-line promise/prefer-await-to-then
      .then((translated) => {
        // eslint-disable-next-line promise/always-return
        if (!storedMessages[locale.value]) {
          storedMessages[locale.value] = {
            [key]: translated,
          };
        } else {
          storedMessages[locale.value] = {
            ...storedMessages[locale.value],
            [key]: translated,
          };
        }
      })
      // eslint-disable-next-line promise/prefer-await-to-callbacks, promise/prefer-await-to-then
      .catch((error) => {
        console.error('Translation error:', error);
      });
  }

  if (
    params.length === 1 &&
    typeof params[0] === 'object' &&
    !Array.isArray(params[0])
  ) {
    // Named interpolation: t('Hello {user}!', { user: 'John Doe' })
    const namedParams = params[0];
    return Object.keys(namedParams).reduce(
      (msg, param) => msg.replaceAll(`{${param}}`, namedParams[param]),
      message,
    );
  }

  if (params.length > 0) {
    // Positional interpolation: t('Hello %s, I am %s', 'John Doe', 'Peter Smith')
    let i = 0;
    return message.replace(/%s/g, () => String(params[i++] ?? ''));
  }

  return message;
};

const setLocale = async (newLocale: Locale): Promise<void> => {
  if (locale.value !== newLocale) {
    const newMessages = await fetchMessages(newLocale);
    locale.value = newLocale;
    storedMessages[newLocale] = newMessages;
    setCookie({ name: 'lang', value: locale.value, path: '/' });
  }
};

export const useI18n = (): I18n => {
  const i18nPlugin = inject<I18n>('i18n');
  if (!i18nPlugin) console.error('I18n Plugin not installed!');

  return {
    t,
    locale,
    setLocale,
    service: i18nService,
  };
};

const createI18n = (): { install: (app: App) => void } => {
  const i18n: I18n = {
    t,
    locale,
    setLocale,
    service: i18nService,
  };

  return {
    install(app: App): void {
      app.config.globalProperties.$i18n = i18n;
      app.config.globalProperties.$t = i18n.t;
      app.provide('i18n', i18n);
    },
  };
};

const i18n = createI18n();

export const createI18nWithExtension = (service: I18nService): typeof i18n => {
  i18nService = service;
  return createI18n();
};

export default i18n;

declare module 'vue' {
  export interface ComponentCustomProperties {
    $i18n: I18n;
    $t: I18n['t'];
  }
}

declare global {
  interface Window {
    i18n: I18n;
  }
}
